<template>
  <div
    ref="tableOfContent"
    class="toc-wrap"
    :class="[{ 'w-64 rounded': smallScreenView && !expanded }]"
  >
    <div
      v-if="!smallScreenView"
      class="toc-items-wrap"
      :class="[{ 'opacity-0': smallScreenView }]"
      :style="tableOfContentHeight"
    >
      <div
        class="toc-toggle-button"
        @click="toggleTableOfContents"
      >
        <span class="text-sm text-text-default">{{ translate(tableOfContentsLabel) }}</span>
        <em
          class="custom-icons flex ml-1 w-4 h-4 bg-icon-default"
          :style="getCustomIconStyle(iconPath(toggleIcon))"
        ></em>
      </div>
      <TableOfContentItems
        class="toc-height"
        :items="items"
        :expanded="expanded"
        :editor-id="editorId"
        :small-screen-view="smallScreenView"
        :editor-prop="editorProp"
        @scrollTo="handleScrollTo"
      />
    </div>
    <v-popover
      v-if="smallScreenView"
      interactive
      tag="div"
      content-tag="div"
      trigger="click"
      :arrow="false"
      :role="'poper'"
      :content-class="'toc-items-dropdown'"
      :placement="'bottom-end'"
    >
      <template #default>
        <div
          v-tooltip="{
            content: translate(tableOfContentsLabel),
            onShow: () => !isMobile,
          }"
          class="toc-button"
          :class="{ '!bg-activeItem-default': expanded }"
          @click="toggleTableOfContents"
        >
          <em
            class="custom-icons flex cursor-pointer w-4 h-4 bg-icon-default"
            :style="getCustomIconStyle(iconPath('list'))"
          >
          </em>
        </div>
      </template>
      <template #content="{ hide }">
        <div
          class="toc-items-wrap-sm flex items-center"
          :style="tocItemsMaxHeight"
        >
          <TableOfContentItems
            :items="items"
            :expanded="expanded"
            :editor-id="editorId"
            :small-screen-view="smallScreenView"
            :editor-prop="editorProp"
            @scrollTo="($event) => handleScrollTo($event, hide)"
          />
        </div>
      </template>
    </v-popover>
  </div>
</template>

<script>
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import TableOfContentItems from './TableOfContentItems.vue';
import EventBus from '@/utilities/eventBus';
import { isPublicPageApp } from '@/utilities/isPublicPageApp';
import { getRootStyle } from '@/utilities/rootStyles';
import { ResourceType } from '@/types/resources';

export default {
  name: 'TableOfContent',

  components: {
    TableOfContentItems,
  },

  props: {
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    editorId: {
      type: String,
      required: false,
      default: '',
    },

    tableOfContentMaxHeightOffset: {
      type: Number,
      required: false,
      default: 0,
    },

    editorProp: {
      type: Object,
      required: false,
      default: null,
    },
  },

  emits: ['scrollTo'],

  setup() {
    const { iconPath, getCustomIconStyle } = useCustomIcon();
    return { iconPath, getCustomIconStyle };
  },

  data() {
    return {
      expanded: true,
      smallScreenView: false,
      ro: null,
    };
  },

  computed: {
    toggleIcon() {
      return this.expanded ? 'chevron-up' : 'chevron-down';
    },

    editor() {
      return this.editorProp ?? this.$store.getters.editor(this.editorId);
    },

    tableOfContentHeight() {
      if (!this.expanded) return 'height: 20px;';
      return `height: calc(100vh - ${this.tableOfContentMaxHeightOffset}px - 70px)`;
    },

    tocItemsMaxHeight() {
      return `max-height: calc(100vh - ${this.tableOfContentMaxHeightOffset}px - 100px)`;
    },

    tableOfContentsLabel() {
      // todo - install i18n and localization package
      return isPublicPageApp() ? 'Table of contents' : 'tableOfContents';
    },
  },

  watch: {
    smallScreenView(newValue) {
      this.expanded = !newValue;
    },
  },

  mounted() {
    if (this.editor) {
      this.ro = new ResizeObserver(this.handleEditorResize);
      this.ro.observe(this.editor.view.dom);
    }

    this.checkIfShouldForceSmallScreenView();
    EventBus.$on('updateFullWidth', this.handleUpdateFullWidth);
  },

  unmounted() {
    if (this.ro) {
      this.ro.disconnect();
    }
    EventBus.$off('updateFullWidth', this.handleUpdateFullWidth);
  },

  methods: {
    toggleTableOfContents() {
      this.expanded = !this.expanded;
    },
    handleScrollTo(id, hideFn) {
      if (this.smallScreenView && this.expanded) {
        this.expanded = false;
      }
      if (typeof hideFn === 'function') {
        hideFn();
      }
      this.$emit('scrollTo', id, 0, 160);
    },
    handleEditorResize() {
      if (!this.editor) return;
      // check if editors content overflowed by table of content element
      const toc = this.$refs.tableOfContent;
      if (toc) {
        const tocRect = toc.getBoundingClientRect();
        const editorRect = this.editor.view.dom.firstElementChild.getBoundingClientRect();
        if (!this.editor.storage.tableOfContent.forceSmallScreenView) {
          const isPageEditor =
            this.editor.resourceType === ResourceType.Page && this.resourceId !== 'pageTemplate';

          const right = isPageEditor
            ? editorRect.right - parseInt(getRootStyle('--page-elements-side-padding'))
            : editorRect.right;
          this.smallScreenView = right >= tocRect.left;
        }
      }
    },

    /*
     * Method to check if any embed board is full width
     * and set smallScreenView to true
     */
    checkIfShouldForceSmallScreenView() {
      if (!this.editor) return false;
      let shouldForceSmallScreenView = false;
      this.editor.view.state.doc.descendants((node) => {
        if (node.type.name === 'embedBoard' && node.attrs['full-width']) {
          this.editor.storage.tableOfContent.forceSmallScreenView = true;
          this.smallScreenView = true;
          shouldForceSmallScreenView = true;
        }
      });

      if (!shouldForceSmallScreenView) {
        this.editor.storage.tableOfContent.forceSmallScreenView = false;
      }

      return shouldForceSmallScreenView;
    },

    handleUpdateFullWidth({ editorId, value }) {
      this.$nextTick(() => {
        if (editorId !== this.editorId) return;
        if (this.checkIfShouldForceSmallScreenView()) return;
        this.smallScreenView = value;
      });
    },
  },
};
</script>

<style lang="scss">
.toc-wrap {
  @apply items-end mr-8 h-0 max-w-64 relative;
  a {
    text-decoration: none;
  }

  .toc-items-wrap {
    @apply absolute box-border max-w-[256px] overflow-hidden bg-background-strongest;
  }

  .toc-height {
    height: calc(100% - (2 * 16px)) !important;
  }

  .toc-toggle-button {
    width: calc(100% - 18px); // 18px is total x-axis padding
    @apply flex flex-row pl-4 items-center cursor-pointer mb-2 hover:bg-activeItem-default rounded px-0.5;
  }

  .toc-button {
    @apply flex items-center justify-center h-6 w-6 rounded ml-auto bg-background-strongest
  border border-solid border-important-border hover:bg-important-hover;
  }
}
.toc-items-dropdown {
  @apply w-[197px] ml-1 shadow-md bg-standard-bg rounded;
  max-width: 256px;

  .toc-items-wrap-sm {
    overflow: hidden;
    flex-direction: column;

    .toc-items-scroll {
      height: 100%;
      @apply w-full !p-4 box-border border border-solid border-important-border rounded;
    }
  }
}
</style>
